import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useIsSuperAdminRole } from './use-system-role';
import { useOrganization } from './api/useOrganization';

export type ViewScope = 'platform' | 'organization' | 'product';

interface ViewScopeResult {
  scope: ViewScope;
  organizationId: string | null;
  productId: string | null;
  /**
   * True enquanto a identidade da organização ainda está sendo carregada
   * (perfil do usuário ou org selecionada do superadmin). Durante esse período
   * o scope é indeterminado e consumidores que decidem navegação/redirect
   * (ex: WorkspaceGuard) devem aguardar em vez de agir sobre o scope transitório.
   */
  isResolving: boolean;
}

export const useViewScope = (): ViewScopeResult => {
  const { isSuperAdmin } = useIsSuperAdminRole();
  const { selectedOrganization, isLoading: isLoadingSuperAdminOrg } = useSuperAdminOrg();
  const { currentWorkspace } = useWorkspace();
  const { data: userProfile, isLoading: isLoadingProfile } = useOrganization();

  const organizationId = isSuperAdmin
    ? selectedOrganization?.id ?? null
    : userProfile?.organization_id ?? null;
  const productId = currentWorkspace?.id ?? null;

  // Antes de o orgId estar resolvido o scope cai em 'organization'/'platform'
  // mesmo que haja um produto selecionado — estado transitório no refresh.
  const isResolving = isSuperAdmin ? isLoadingSuperAdminOrg : isLoadingProfile;

  // 'platform' scope só faz sentido pra superadmin. Pra usuários comuns,
  // mid-loading do profile pode deixar organizationId=null momentaneamente —
  // sem o guard, o hook responderia 'platform' e disparava queries que
  // retornariam 403. Pra não-superadmin, mantém 'organization' mesmo sem
  // orgId resolvido (queries downstream devem checar e desabilitar).
  let scope: ViewScope;
  if (!organizationId) {
    scope = isSuperAdmin ? 'platform' : 'organization';
  } else if (!productId) {
    scope = 'organization';
  } else {
    scope = 'product';
  }

  return { scope, organizationId, productId, isResolving };
};
